import { useState, useEffect, useCallback } from "react";
import modelsHelper from "../API/Models/modelsHelper";

// Cache for models data to avoid repeated fetches
let cachedModelsData = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const useModelsPricing = () => {
  const [modelsData, setModelsData] = useState(cachedModelsData);
  const [isLoading, setIsLoading] = useState(!cachedModelsData);
  const [error, setError] = useState(null);

  const fetchModelsData = useCallback(async (forceRefresh = false) => {
    // Check cache validity
    const now = Date.now();
    if (
      !forceRefresh &&
      cachedModelsData &&
      cacheTimestamp &&
      now - cacheTimestamp < CACHE_DURATION
    ) {
      setModelsData(cachedModelsData);
      setIsLoading(false);
      return cachedModelsData;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await modelsHelper.getModelsList();

      // Update cache
      cachedModelsData = data;
      cacheTimestamp = now;

      setModelsData(data);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModelsData();
  }, [fetchModelsData]);

  /**
   * Get pricing info for a specific model
   * @param {string} modelId - The model identifier (e.g., "sorcerer", "mystica")
   * @returns {Object|null} Pricing info with UoI values
   */
  const getModelPricing = useCallback(
    (modelId) => {
      if (!modelsData?.templates) {
        // Fallback to medium tier pricing
        return {
          modelType: "medium",
          inputUoiPer1mTokens: modelsHelper.usdToUoi(0.62),
          outputUoiPer1mTokens: modelsHelper.usdToUoi(1.75),
        };
      }

      const modelInfo = modelsData.templates[modelId];

      if (!modelInfo) {
        // Fallback to medium tier pricing
        return {
          modelType: "medium",
          inputUoiPer1mTokens: modelsHelper.usdToUoi(0.62),
          outputUoiPer1mTokens: modelsHelper.usdToUoi(1.75),
        };
      }

      const pricing = modelInfo.pricing;
      if (!pricing) {
        // Fallback to medium tier pricing
        return {
          modelType: modelInfo.modelType || "medium",
          inputUoiPer1mTokens: modelsHelper.usdToUoi(0.62),
          outputUoiPer1mTokens: modelsHelper.usdToUoi(1.75),
        };
      }

      return {
        name: modelInfo.name,
        modelType: modelInfo.modelType || "medium",
        pricingTier: modelInfo.pricingTier || null,
        maxTokens: modelInfo.maxTokens,
        dynamicRouting: modelInfo.dynamicRouting,
        reasoner: modelInfo.reasoner,
        health: modelInfo.health,
        inputPer1mTokens: pricing.inputPer1mTokens,
        outputPer1mTokens: pricing.outputPer1mTokens,
        inputUoiPer1mTokens: modelsHelper.usdToUoi(pricing.inputPer1mTokens),
        outputUoiPer1mTokens: modelsHelper.usdToUoi(pricing.outputPer1mTokens),
        // Prompt-cache pricing (model-level conservative ratio from the
        // backend). Absent when the vendor ratio is unverified — the UI
        // hides the cached line in that case.
        cachedInputRatio: pricing.cachedInputRatio ?? null,
        cachedInputUoiPer1mTokens:
          pricing.cachedInputPer1mTokens != null
            ? modelsHelper.usdToUoi(pricing.cachedInputPer1mTokens)
            : null,
      };
    },
    [modelsData]
  );

  /**
   * Get image pricing info by tier
   * @param {string} tier - The tier (e.g., "small", "medium", "large")
   * @returns {Object|null} Pricing info with UoI values
   */
  const getImagePricingByTier = useCallback(
    (tier = "medium") => {
      if (!modelsData?.imagePricing) return null;
      const pricing = modelsData.imagePricing[tier];
      if (!pricing) return null;

      return {
        modelType: tier,
        perImage: pricing.perImage,
        perImageUoi: modelsHelper.usdToUoi(pricing.perImage),
      };
    },
    [modelsData]
  );

  /**
   * Get image model pricing by model ID
   * @param {string} modelId - The model ID (e.g., "black-forest-labs/flux-dev")
   * @returns {Object|null} Pricing info with UoI values
   */
  const getImageModelPricing = useCallback(
    (modelId) => {
      if (!modelsData?.imageModels || !modelId) return null;
      const modelInfo = modelsData.imageModels[modelId];
      if (!modelInfo) return getImagePricingByTier("medium"); // Default fallback

      return {
        modelType: modelInfo.modelType,
        perImage: modelInfo.perImage,
        perImageUoi: modelsHelper.usdToUoi(modelInfo.perImage),
      };
    },
    [modelsData, getImagePricingByTier]
  );

  /**
   * Get video pricing info by tier
   * @param {string} tier - The tier (e.g., "small", "medium", "large")
   * @returns {Object|null} Pricing info with UoI values
   */
  const getVideoPricingByTier = useCallback(
    (tier = "medium") => {
      if (!modelsData?.videoPricing) return null;
      const pricing = modelsData.videoPricing[tier];
      if (!pricing) return null;

      return {
        modelType: tier,
        perVideo: pricing.perVideo,
        perVideoUoi: modelsHelper.usdToUoi(pricing.perVideo),
      };
    },
    [modelsData]
  );

  /**
   * Get video model pricing by model ID
   * @param {string} modelId - The model ID (e.g., "minimax/video-01")
   * @returns {Object|null} Pricing info with UoI values
   */
  const getVideoModelPricing = useCallback(
    (modelId) => {
      if (!modelsData?.videoModels || !modelId) return null;
      const modelInfo = modelsData.videoModels[modelId];
      if (!modelInfo) return getVideoPricingByTier("medium"); // Default fallback

      return {
        modelType: modelInfo.modelType,
        perVideo: modelInfo.perVideo,
        perVideoUoi: modelsHelper.usdToUoi(modelInfo.perVideo),
      };
    },
    [modelsData, getVideoPricingByTier]
  );

  /**
   * Get audio TTS pricing by model ID
   * @param {string} modelId - The model ID (e.g., "orpheus-fast", "orpheus", "kokoro")
   * @returns {Object|null} Pricing info with UoI values
   */
  const getAudioTtsPricing = useCallback(
    (modelId = "default") => {
      if (!modelsData?.audioTtsModels || !modelId) {
        // Fallback to tier-based pricing
        if (!modelsData?.audioTtsPricing) return null;
        const pricing = modelsData.audioTtsPricing["default"];
        if (!pricing) return null;
        return {
          modelType: "medium",
          per1mCharacters: pricing.per1mCharacters,
          per1mCharactersUoi: modelsHelper.usdToUoi(pricing.per1mCharacters),
        };
      }

      const modelInfo = modelsData.audioTtsModels[modelId];
      if (!modelInfo) {
        // Try tier-based fallback
        const pricing = modelsData.audioTtsPricing?.["default"];
        if (!pricing) return null;
        return {
          modelType: "medium",
          per1mCharacters: pricing.per1mCharacters,
          per1mCharactersUoi: modelsHelper.usdToUoi(pricing.per1mCharacters),
        };
      }

      return {
        modelType: modelInfo.modelType,
        pricingKey: modelInfo.pricingKey,
        per1mCharacters: modelInfo.per1mCharacters,
        per1mCharactersUoi: modelsHelper.usdToUoi(modelInfo.per1mCharacters),
      };
    },
    [modelsData]
  );

  /**
   * Get audio transcription pricing by model ID
   * @param {string} modelId - The model ID (e.g., "whisper-large-v3", "whisper-large-v3-turbo")
   * @returns {Object|null} Pricing info with UoI values
   */
  const getAudioTranscriptionPricing = useCallback(
    (modelId = "default") => {
      if (!modelsData?.audioTranscriptionModels || !modelId) {
        // Fallback to tier-based pricing
        if (!modelsData?.audioTranscriptionPricing) return null;
        const pricing = modelsData.audioTranscriptionPricing["default"];
        if (!pricing) return null;
        return {
          modelType: "medium",
          perMinute: pricing.perMinute,
          perMinuteUoi: modelsHelper.usdToUoi(pricing.perMinute),
        };
      }

      const modelInfo = modelsData.audioTranscriptionModels[modelId];
      if (!modelInfo) {
        // Try tier-based fallback
        const pricing = modelsData.audioTranscriptionPricing?.["default"];
        if (!pricing) return null;
        return {
          modelType: "medium",
          perMinute: pricing.perMinute,
          perMinuteUoi: modelsHelper.usdToUoi(pricing.perMinute),
        };
      }

      return {
        modelType: modelInfo.modelType,
        pricingKey: modelInfo.pricingKey,
        perMinute: modelInfo.perMinute,
        perMinuteUoi: modelsHelper.usdToUoi(modelInfo.perMinute),
      };
    },
    [modelsData]
  );

  /**
   * Get 3D model pricing by tier
   * @param {string} tier - The tier
   * @returns {Object|null} Pricing info with UoI values
   */
  const get3dModelPricingByTier = useCallback(
    (tier = "medium") => {
      if (!modelsData?.model3dPricing) return null;
      const pricing = modelsData.model3dPricing[tier];
      if (!pricing) return null;

      return {
        modelType: tier,
        perModel: pricing.perModel,
        perModelUoi: modelsHelper.usdToUoi(pricing.perModel),
      };
    },
    [modelsData]
  );

  /**
   * Get 3D model pricing by model ID
   * @param {string} modelId - The model ID (e.g., "firtoz/trellis")
   * @returns {Object|null} Pricing info with UoI values
   */
  const get3dModelPricing = useCallback(
    (modelId) => {
      if (!modelsData?.model3dModels || !modelId) return get3dModelPricingByTier("medium");
      const modelInfo = modelsData.model3dModels[modelId];
      if (!modelInfo) return get3dModelPricingByTier("medium"); // Default fallback

      return {
        modelType: modelInfo.modelType,
        perModel: modelInfo.perModel,
        perModelUoi: modelsHelper.usdToUoi(modelInfo.perModel),
      };
    },
    [modelsData, get3dModelPricingByTier]
  );

  return {
    modelsData,
    isLoading,
    error,
    fetchModelsData,
    // LLM pricing
    getModelPricing,
    // Image pricing
    getImagePricingByTier,
    getImageModelPricing,
    // Video pricing
    getVideoPricingByTier,
    getVideoModelPricing,
    // Audio TTS pricing
    getAudioTtsPricing,
    // Audio transcription pricing
    getAudioTranscriptionPricing,
    // 3D model pricing
    get3dModelPricingByTier,
    get3dModelPricing,
    // Utility
    formatUoi: modelsHelper.formatUoi,
  };
};

export default useModelsPricing;
